import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Smartphone, X } from "lucide-react";
import api from "../utils/api";

export default function MobileAccessModal({ onClose, onVerified }) {
  const [number, setNumber] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const dialog = useRef(null);
  const pending = useRef(false);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const element = dialog.current;
    element.querySelector("input").focus();
    const keydown = (event) => {
      if (event.key === "Escape" && !pending.current) onClose();
      if (event.key === "Tab") {
        const controls = [...element.querySelectorAll("button:not(:disabled), input:not(:disabled)")];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", keydown);
      previousFocus?.focus();
    };
  }, [onClose]);

  const valid = /^(?:[789]\d{9}|0[789]\d{9}|234[789]\d{9})$/.test(number);
  const submit = async (event) => {
    event.preventDefault();
    if (!valid || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/content-session", { phoneNumber: number });
      if (!data.success || !data.data?.token) throw new Error("Unable to verify subscription");
      localStorage.setItem("accessToken", data.data.token);
      localStorage.setItem("isLoggedIn", "true");
      localStorage.removeItem("user");
      localStorage.removeItem("subscription");
      window.dispatchEvent(new Event("auth-state-changed"));
      onVerified();
    } catch (failure) {
      setError(failure.response?.data?.message || "Unable to verify your number. Please try again.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-4" onClick={() => !busy && onClose()}>
      <section ref={dialog} role="dialog" aria-modal="true" aria-labelledby="mobile-access-title" className="w-full max-w-[560px] overflow-hidden rounded-2xl border border-gray-700 bg-[#181820] shadow-2xl" onClick={event => event.stopPropagation()}>
        <header className="relative flex items-center gap-4 bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-6 text-white">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/20"><Smartphone size={28} /></span>
          <div className="pr-8"><h2 id="mobile-access-title" className="m-0 text-2xl font-extrabold">Login to Continue</h2><p className="mb-0 mt-1 text-sm sm:text-base">Enter your mobile number to access content</p></div>
          <button type="button" aria-label="Close" disabled={busy} onClick={onClose} className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/25"><X size={22} /></button>
        </header>
        <form onSubmit={submit} className="p-6 sm:p-8">
          <label htmlFor="mobile-access-number" className="mb-2 block font-semibold text-gray-300">Mobile Number</label>
          <div className="flex overflow-hidden rounded-xl border border-yellow-500 bg-[#0f0f18] focus-within:ring-2 focus-within:ring-yellow-500">
            <span className="flex items-center border-r border-gray-600 bg-[#242432] px-5 text-xl font-bold text-white">+234</span>
            <input id="mobile-access-number" type="tel" inputMode="numeric" autoComplete="tel-national" placeholder="e.g. 8012345678" value={number} disabled={busy} maxLength={18} onChange={event => { setNumber(event.target.value.replace(/\D/g, "")); setError(""); }} aria-invalid={Boolean(error)} aria-describedby={error ? "mobile-access-error" : undefined} className="min-w-0 flex-1 bg-transparent px-4 py-4 text-lg text-white outline-none placeholder:text-slate-500" />
          </div>
          {error && <p id="mobile-access-error" role="alert" className="mb-0 mt-3 text-sm text-red-400">{error}</p>}
          <button type="submit" disabled={!valid || busy} className="mt-6 w-full rounded-xl bg-yellow-500 py-4 text-lg font-bold text-[#181820] transition hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-40">{busy ? "Verifying..." : "Continue"}</button>
        </form>
      </section>
    </div>, document.body
  );
}
